import apiUtils from '../utils/apiUtils';
import enhancedAuthService from './enhanced-auth-service';

/**
 * Ad Settings Service
 * Manages global ad configuration (admin-controlled)
 */
export interface AdSettings {
  amazonTAMEnabled: boolean;
  prebidEnabled: boolean;
  personalizedAdsEnabled: boolean;
  adRefreshEnabled: boolean;
  debugMode: boolean;
  reviveEnabled: boolean;
  reviveServerUrl: string;
  reviveZones: {
    header: number;
    sidebar: number;
    mobile: number;
    inContent: number;
    footer: number;
  };
  lastUpdatedBy?: string;
  lastUpdatedAt?: string;
}

export class AdSettingsService {
  private static instance: AdSettingsService;
  private settings: AdSettings | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private lastFetch = 0;
  private readonly ADMIN_EMAIL = 'akamboh@gmail.com';
  private readonly ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

  private constructor() {}

  public static getInstance(): AdSettingsService {
    if (!AdSettingsService.instance) {
      AdSettingsService.instance = new AdSettingsService();
    }
    return AdSettingsService.instance;
  }

  /**
   * Get default settings
   */
  private getDefaultSettings(): AdSettings {
    return {
      amazonTAMEnabled: true,
      prebidEnabled: true,
      personalizedAdsEnabled: true,
      adRefreshEnabled: true,
      debugMode: false,
      reviveEnabled: false,
      reviveServerUrl: 'http://localhost/revive',
      reviveZones: {
        header: 1,
        sidebar: 2,
        mobile: 3,
        inContent: 4,
        footer: 5,
      },
    };
  }

  /**
   * Load global settings from backend
   */
  async loadSettings(): Promise<AdSettings> {
    try {
      // Check cache first
      const now = Date.now();
      if (this.settings && (now - this.lastFetch) < this.CACHE_DURATION) {
        return this.settings;
      }

      // Load global settings from backend
      const response = await fetch(`${this.ADMIN_API_URL}/admin/ad-settings`);
      const data = await response.json();
      
      if (data.success && data.settings) {
        this.settings = data.settings;
        this.lastFetch = now;
        return this.settings!;
      }

      // Use defaults if backend fails
      this.settings = this.getDefaultSettings();
      this.lastFetch = now;
      return this.settings;

    } catch (error) {
      console.error('Error loading global ad settings:', error);
      // Fallback to defaults
      this.settings = this.getDefaultSettings();
      return this.settings;
    }
  }

  /**
   * Save global settings (admin only)
   */
  async saveSettings(settings: Partial<AdSettings>): Promise<void> {
    try {
      // Check if user is admin
      const user = await this.getCurrentUser();
      if (!user || user.email !== this.ADMIN_EMAIL) {
        throw new Error('Only admin can modify global ad settings');
      }

      // Merge with existing settings
      const currentSettings = await this.loadSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      
      // Get JWT token from auth service
      const token = await enhancedAuthService.getAccessToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Save to backend
      const response = await fetch(`${this.ADMIN_API_URL}/admin/ad-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          settings: updatedSettings
        })
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to save global ad settings');
      }
      
      // Update cache
      this.settings = updatedSettings;
      this.lastFetch = Date.now();
      
      console.log('Global ad settings saved by admin:', updatedSettings);
      
    } catch (error) {
      console.error('Error saving global ad settings:', error);
      throw error;
    }
  }

  /**
   * Get current user (for admin check)
   */
  private async getCurrentUser(): Promise<any> {
    try {
      const response = await apiUtils.get('/auth/me') as any;
      return response.user;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Check if current user is admin
   */
  async isAdmin(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return user && user.email === this.ADMIN_EMAIL;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  /**
   * Get current settings
   */
  async getSettings(): Promise<AdSettings> {
    return await this.loadSettings();
  }

  /**
   * Clear settings cache (admin only)
   */
  clearSettings(): void {
    try {
      this.settings = null;
      this.lastFetch = 0;
      console.log('Ad settings cache cleared');
    } catch (error) {
      console.error('Error clearing ad settings cache:', error);
    }
  }

  /**
   * Check if Amazon TAM is enabled
   */
  async isAmazonTAMEnabled(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.amazonTAMEnabled;
  }

  /**
   * Check if Revive Ad Server is enabled
   */
  async isReviveEnabled(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.reviveEnabled;
  }

  /**
   * Get Revive server URL
   */
  async getReviveServerUrl(): Promise<string> {
    const settings = await this.getSettings();
    return settings.reviveServerUrl;
  }

  /**
   * Get Revive zones configuration
   */
  async getReviveZones(): Promise<AdSettings['reviveZones']> {
    const settings = await this.getSettings();
    return { ...settings.reviveZones };
  }

  /**
   * Get Revive configuration for the ReviveAdService
   */
  async getReviveConfig(): Promise<{
    enabled: boolean;
    serverUrl: string;
    zones: AdSettings['reviveZones'];
    fallbackAds: { enabled: boolean; defaultBanners: string[] };
  }> {
    const settings = await this.getSettings();
    return {
      enabled: settings.reviveEnabled,
      serverUrl: settings.reviveServerUrl,
      zones: settings.reviveZones,
      fallbackAds: {
        enabled: true,
        defaultBanners: [], // Will use default fashion ads from ReviveAdService
      },
    };
  }

  /**
   * Get debug information
   */
  async getDebugInfo(): Promise<Record<string, any>> {
    const settings = await this.getSettings();
    const isAdmin = await this.isAdmin();
    return {
      settings,
      isAdmin,
      cacheAge: Date.now() - this.lastFetch,
      adminApiUrl: this.ADMIN_API_URL,
      environment: process.env.NODE_ENV,
    };
  }
}

// Export singleton instance
export const adSettingsService = AdSettingsService.getInstance();